import { useState } from 'react';
import { FileCode, ArrowLeftRight, Copy, CheckCircle2, AlertCircle } from 'lucide-react';

export default function YAMLConverter() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<'yaml-to-json' | 'json-to-yaml'>('yaml-to-json');

  // Simple YAML to JSON converter (basic implementation)
  const yamlToJson = (yaml: string): string => {
    try {
      // This is a simplified YAML parser for basic cases
      // For production, use js-yaml library
      const lines = yaml.trim().split('\n');
      const result: any = {};

      lines.forEach(line => {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) return;

        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();

        if (value) {
          // Try to parse as number or boolean
          let parsedValue: any = value;
          if (value === 'true') parsedValue = true;
          else if (value === 'false') parsedValue = false;
          else if (!isNaN(Number(value)) && value !== '') parsedValue = Number(value);
          else if (value.startsWith('"') && value.endsWith('"')) {
            parsedValue = value.slice(1, -1);
          }

          result[key] = parsedValue;
        }
      });

      return JSON.stringify(result, null, 2);
    } catch (err) {
      throw new Error('Failed to parse YAML. This is a basic parser - use js-yaml for complex YAML.');
    }
  };

  // Simple JSON to YAML converter
  const jsonToYaml = (json: string): string => {
    try {
      const obj = JSON.parse(json);
      return objectToYaml(obj, 0);
    } catch (err) {
      throw new Error('Invalid JSON');
    }
  };

  const objectToYaml = (obj: any, indent: number): string => {
    const spaces = '  '.repeat(indent);
    let yaml = '';

    for (const [key, value] of Object.entries(obj)) {
      if (value === null) {
        yaml += `${spaces}${key}: null\n`;
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        yaml += `${spaces}${key}:\n${objectToYaml(value, indent + 1)}`;
      } else if (Array.isArray(value)) {
        yaml += `${spaces}${key}:\n`;
        value.forEach(item => {
          if (typeof item === 'object') {
            yaml += `${spaces}  -\n${objectToYaml(item, indent + 2)}`;
          } else {
            yaml += `${spaces}  - ${item}\n`;
          }
        });
      } else if (typeof value === 'string') {
        yaml += `${spaces}${key}: "${value}"\n`;
      } else {
        yaml += `${spaces}${key}: ${value}\n`;
      }
    }

    return yaml;
  };

  const convert = () => {
    try {
      setError('');
      if (mode === 'yaml-to-json') {
        setOutput(yamlToJson(input));
      } else {
        setOutput(jsonToYaml(input));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Conversion failed');
      setOutput('');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <FileCode className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">YAML ↔ JSON Converter</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Convert between YAML and JSON formats</p>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 mb-4">
        <button
          onClick={() => setMode('yaml-to-json')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'yaml-to-json'
              ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg'
              : 'bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text'
          }`}
        >
          YAML → JSON
        </button>
        <ArrowLeftRight className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => setMode('json-to-yaml')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            mode === 'json-to-yaml'
              ? 'bg-gradient-to-r from-coral-500 to-teal-500 text-white shadow-lg'
              : 'bg-gray-100 dark:bg-dark-bg text-gray-700 dark:text-dark-text'
          }`}
        >
          JSON → YAML
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
            Input ({mode === 'yaml-to-json' ? 'YAML' : 'JSON'})
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={mode === 'yaml-to-json' ? 'name: John\nage: 30' : '{"name": "John", "age": 30}'}
            className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
          />
          <button
            onClick={convert}
            className="w-full mt-2 px-4 py-2 bg-gradient-to-r from-coral-500 to-teal-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all"
          >
            Convert
          </button>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-400">
              Output ({mode === 'yaml-to-json' ? 'JSON' : 'YAML'})
            </label>
            {output && (
              <button
                onClick={copyToClipboard}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
              >
                {copied ? (
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
              </button>
            )}
          </div>
          <textarea
            value={output}
            readOnly
            placeholder="Converted output will appear here..."
            className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-xs bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-dark-text resize-none"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <p className="text-xs text-red-500 dark:text-red-400 mt-1">
              Note: This is a basic parser. For complex YAML, consider using a dedicated library.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
